import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon, channelIconName } from '@/components/ui/Icon';
import { Tooltip } from '@/components/ui/Tooltip';
import { initialsOf } from '@/components/console/Avatar';
import { StateBadge } from '@/components/console/StateBadge';
import { CrmCategoryPicker } from '@/components/console/workspace/CrmCategoryPicker';
import { ConversationCategoryPicker } from '@/components/console/workspace/ConversationCategoryPicker';
import { ConversationProperties } from '@/components/console/workspace/ConversationProperties';
import { ContactSummary } from '@/components/console/workspace/ContactSummary';
import { ConversationSubject } from '@/components/console/workspace/ConversationSubject';
import { TagPicker } from '@/components/console/workspace/TagPicker';
import { ContinuesLink } from '@/components/console/workspace/ContinuesLink';
import { PastConversations } from '@/components/console/workspace/PastConversations';
import { TraceIds } from '@/components/console/workspace/TraceIds';
import { avatarColor, hueOf } from '@/components/console/workspace/MsgAvatar';
import { useT } from '@/i18n/labels';
import { useAgentStore } from '@/stores/agentStore';
import { useAuthStore } from '@/stores/authStore';
import { useConversationStore } from '@/stores/conversationStore';
import { useCustomerProfile } from '@/hooks/useCustomerProfile';
import { usePersistedFlag } from '@/hooks/usePersistedFlag';
import { usePermission } from '@/hooks/usePermission';
import { formatDateTime, formatRelative } from '@/utils/formatters';
import { getCustomerDisplayName, isAnonymousCustomer } from '@/utils/conversationLabel';
import { useVerifiedOwner } from '@/console/useVerifiedOwner';
import { isOwnershipEnforced, lockedByOtherOwner, ownerDisplay } from '@/utils/ownership';
import type { Conversation } from '@/types';

/** localStorage keys. The collapse key is NEW (console v2); the contact-section key predates it and keeps its name. */
export const CONTACT_COLLAPSED_KEY = 'ac-contact-collapsed';
export const CONTACT_SECTION_OPEN_KEY = 'ac-ctx-contact-open';
export const DETAILS_SECTION_OPEN_KEY = 'ac-cp-details-open';

interface ContactPanelProps {
  conv: Conversation;
}

/** One label/value row in the details list. */
const Kv: React.FC<{ label: string; testId: string; children: React.ReactNode }> = ({
  label,
  testId,
  children,
}) => (
  <div className="ac-ctx-kv" data-testid={testId}>
    <span className="ac-ctx-kv-k">{label}</span>
    <span className="ac-ctx-kv-v">{children}</span>
  </div>
);

/**
 * The right-hand panel: who the customer is, who owns THIS conversation, and what this conversation is.
 *
 * From the top: the contact card (stable-colour initials, the customer's name, the channel), the owner row, then
 * the existing sections - the conversation's subject and details, the tenant's CRM category / properties, and the
 * contact section - each unchanged in behaviour. The conversation details and the contact section collapse, and the
 * whole panel collapses to a 48px strip that a tooltip names (a collapsed panel with no label is a mystery button).
 *
 * <b>The card is built from what the CONVERSATION knows - it makes no request.</b> The CRM profile (email, phone,
 * company) lives in the collapsed contact section and is fetched only when that is opened: leaving it expanded would
 * fire a CRM request for every conversation an agent clicks. Opening it is one click and the choice persists.
 *
 * <b>Owner, never a raw id.</b> 'You' for the signed-in agent, the directory name for a teammate, 'Team shared' when
 * nobody owns it (or the owner is not yet verified). An id the directory cannot resolve (a deactivated colleague, a failed directory fetch) shows the
 * generic agent label with the id in the tooltip - honest about "someone owns this" without printing a GUID. When
 * someone ELSE owns the conversation - or a human holds it and nobody can yet be named - every control that edits
 * it is disabled: it is shown, not editable.
 *
 * <b>Two category axes, deliberately both here.</b> The tenant's Botablex conversation-state (ADR-017
 * `conversation_state_defs`) answers "where is this in our process", and the tenant's CRM category answers "what is
 * this about" in their own taxonomy. They were kept separate by design, so the panel shows both.
 *
 * <b>Neither category control fetches its vocabulary here.</b> The conversation-state picker reads the defs
 * ConsoleShell already loaded once into `conversationStore.stateDefs` (a test asserts they are never re-requested);
 * the CRM picker fetches its own per-session state because the selection lives in the tenant's CRM, not in our store.
 */
export const ContactPanel: React.FC<ContactPanelProps> = ({ conv }) => {
  const { t } = useT();
  const navigate = useNavigate();
  const directory = useAgentStore((s) => s.directory);
  const myAgentId = useAuthStore((s) => s.user?.agentId);
  // The VERIFIED owner - the very one the chat view reads (console/useVerifiedOwner.ts): null while a fallback owner
  // is not yet confirmed by the server. Called before the collapsed early return, as hook rules require.
  const { owner: ownerId, holder, ownerUnverified } = useVerifiedOwner(conv);
  const { hasPermission, isTenantAdmin } = usePermission();

  const [collapsed, toggleCollapsed] = usePersistedFlag(CONTACT_COLLAPSED_KEY, false);
  // The conversation's own details are open by default (they are what the panel is for); a stored '0' keeps them shut.
  const [detailsOpen, toggleDetails] = usePersistedFlag(DETAILS_SECTION_OPEN_KEY, true);
  // Contact basics start CLOSED, for the cost reason in the doc comment above.
  const [contactOpen, toggleContact] = usePersistedFlag(CONTACT_SECTION_OPEN_KEY, false);
  const closedEditAllowedByTenant = useConversationStore((s) => s.consoleSettings?.editClosedConversations === true);

  const anon = isAnonymousCustomer(conv.customerId);
  const knownName = anon ? t('ctx_anon_title') : getCustomerDisplayName(conv);
  const shownName = knownName || t('unidentified');

  // Passing null while the section is closed is what makes it free - the hook makes no request for a falsy id. The
  // hook is still CALLED unconditionally, as hook rules require.
  const profileState = useCustomerProfile(contactOpen ? conv.customerId : null);

  // Collapsed: a thin strip with one button, named by a tooltip (hover AND keyboard focus).
  if (collapsed) {
    const showLabel = t('cp_show');
    return (
      <aside className="cp collapsed" aria-label={t('cp_title')} data-testid="customer-context-collapsed">
        <Tooltip label={knownName ? `${showLabel} · ${knownName}` : showLabel} side="start">
          <button
            type="button"
            className="cp-reopen"
            onClick={toggleCollapsed}
            aria-label={showLabel}
            aria-expanded={false}
            data-testid="ctx-expand"
          >
            <Icon name="chevronStart" size={18} />
            <span className="cp-avatar sm" style={{ background: avatarColor(conv.sessionId) }} aria-hidden="true">
              {initialsOf(knownName)}
            </span>
          </button>
        </Tooltip>
      </aside>
    );
  }

  // Owner: ONE verified derivation shared with the chat header and the answer bar. An owner we cannot match to the
  // signed-in agent is someone ELSE, and a human-controlled conversation whose owner is UNKNOWN (nobody named, or not
  // yet verified) is read-only too: unknown fails closed, so every editor below stays disabled until the owner is known.
  const mine = !!ownerId && !!myAgentId && ownerId === myAgentId;
  // The same lock as the chat header: private ownership in effect (wire `ownershipEnforced`), or - before PR-A2 - a live
  // takeover by a teammate. A recorded owner of a bot-handled row locks nobody and earns no privacy badge.
  const enforced = isOwnershipEnforced(conv);
  // WHO holds it is the HOLDER (utils/ownership.ts holderId): the owner under private ownership, the live controller in
  // legacy shared mode, where a recorded owner may not be the person who took the conversation over.
  const ownedByOther = lockedByOtherOwner(conv, holder, myAgentId);
  // `ownerUnverified` is also true for a row the server never described (a push or draft placeholder not yet re-read):
  // not known to be mine, so no editor is open until it has been.
  // Operator 2026-11-01: when the tenant lets every agent edit CLOSED conversations, the owner lock does not apply to the editors below
  // (tags, status, subject, CRM ticket properties) once the conversation is closed. A display hint only: the server decides each save.
  const closedEditOpen = conv.state === 'CLOSED' && closedEditAllowedByTenant;
  const editLocked = !closedEditOpen && (ownedByOther || ownerUnverified || (conv.state === 'HUMAN_TAKEOVER' && !holder));

  // The same owner-name rule as the chat header (utils/ownership.ts ownerDisplay).
  const { text: ownerText, title: ownerTitle } = ownerDisplay(ownerId, myAgentId, directory, t);

  return (
    // No entrance animation (ac-fade-up) on this element: its `forwards` fill leaves a transform in place, which makes
    // the aside the containing block for the tooltips' position:fixed tips and shifts them by the aside's own offset.
    // The tips below open on the 'start' side because the panel sits against the inline-end edge of the window.
    <aside className="cp" aria-label={t('cp_title')} data-testid="conversation-context">
      <div className="cp-head">
        <h2 className="cp-title">{t('cp_title')}</h2>
        <Tooltip label={t('cp_hide')} side="start">
          <button
            type="button"
            className="ac-rail-toggle"
            onClick={toggleCollapsed}
            aria-label={t('cp_hide')}
            aria-expanded={true}
            data-testid="ctx-collapse"
          >
            <Icon name="chevronEnd" size={16} />
          </button>
        </Tooltip>
      </div>

      <div className="cp-scroll">
        <div className="cp-card" data-testid="cp-card">
          <span
            className="cp-avatar"
            style={{ background: avatarColor(conv.sessionId) }}
            data-hue={hueOf(conv.sessionId)}
            aria-hidden="true"
            data-testid="cp-avatar"
          >
            {initialsOf(knownName)}
          </span>
          <div className="cp-card-main">
            <div className="cp-name" dir="auto" data-testid="cp-name">
              {shownName}
            </div>
            <div className="cp-role">
              <Icon name={channelIconName(conv.channel)} size={13} />
              {conv.channel}
            </div>
          </div>
        </div>

        <div className="cp-owner-row" data-testid="conv-ctx-owner">
          <span className="cp-owner-k">{t('conv_owner')}</span>
          <span className={`cp-owner${mine ? ' mine' : ''}${!ownerId ? ' team' : ''}`}>
            <Icon name={ownerId && enforced ? 'lock' : 'users'} size={12} />
            <span title={ownerTitle} dir="auto">
              {ownerText}
            </span>
          </span>
        </div>

        {/* D7: when the server linked this conversation to the customer's earlier CLOSED one, say so and offer the way
            back. Reads only the row's server-stamped previousSessionId; renders nothing without it. */}
        <ContinuesLink previousSessionId={conv.previousSessionId} testId="cp-continues-link" />

        <ConversationSubject conv={conv} disabled={editLocked} />

        {/* The conversation's tags (ADR-033 D8): attach / detach EXISTING catalog tags; the catalog is the admin's. Editing
            needs workspace.handle (a viewAll-only reader sees the chips) and a conversation that is not locked to someone
            else - except for a tenant admin, who may tag a teammate's conversation too (2026-10-19). Closed stays editable. Keyed on the session so nothing in flight carries over to the next one. */}
        <div className="ac-ctx-sec" data-testid="cp-tags">
          <div className="ac-ctx-sec-title">{t('ctx_tags')}</div>
          <TagPicker
            key={conv.sessionId}
            sessionId={conv.sessionId}
            selected={Array.isArray(conv.tags) ? conv.tags : []}
            readOnly={(editLocked && !isTenantAdmin) || !hasPermission('workspace.handle')}
            testIdPrefix="cp-tags"
          />
        </div>

        <div className="ac-ctx-sec">
          <button
            type="button"
            className="ac-ctx-sec-toggle"
            onClick={toggleDetails}
            aria-expanded={detailsOpen}
            data-testid="cp-details-toggle"
          >
            <span className="ac-ctx-sec-title">{t('conv_ctx_title')}</span>
            <Icon name="chevron" size={14} />
          </button>

          {detailsOpen && (
            <div className="cp-sec-body" data-testid="conv-details">
              <Kv label={t('conv_status')} testId="conv-ctx-status">
                <StateBadge state={conv.state} />
              </Kv>

              <Kv label={t('conv_channel')} testId="conv-ctx-channel">
                <span className="ac-ctx-inline">
                  <Icon name={channelIconName(conv.channel)} size={13} />
                  {conv.channel}
                </span>
              </Kv>

              <Kv label={t('conv_state_category')} testId="conv-ctx-state-category">
                <ConversationCategoryPicker conv={conv} disabled={editLocked} />
              </Kv>

              <Kv label={t('conv_created')} testId="conv-ctx-created">
                {formatDateTime(conv.createdAt)}
              </Kv>

              <Kv label={t('conv_last_message')} testId="conv-ctx-last-message">
                {formatRelative(conv.lastMessageTimestamp)}
              </Kv>

              {conv.state === 'CLOSED' && conv.closedAt && (
                <Kv label={t('conv_closed')} testId="conv-ctx-closed">
                  {formatDateTime(conv.closedAt)}
                </Kv>
              )}
            </div>
          )}
        </div>

        {/*
          The tenant's own CRM category on this conversation (CRM v2 WS-6.1).

          Keyed on the SESSION, not the customer: a ticket exists per conversation, so a customer-keyed picker would
          have nothing to address. OUTSIDE any anonymous branch on purpose - a ticket exists for an anonymous visitor
          too, and it is precisely those conversations (no name, no history, nothing else to go on) where a category
          is most worth setting. It renders nothing when the tenant has no category mapped.
        */}
        <CrmCategoryPicker sessionId={conv.sessionId} disabled={editLocked} />

        {/*
          The tenant's own ticket properties (its own collapsible section, persisted). Sits with the CRM category
          rather than in the contact section because both describe this CONVERSATION in the tenant's CRM, while the
          contact block below widens to the person. Renders nothing when the tenant has configured none.
        */}
        <ConversationProperties sessionId={conv.sessionId} disabled={editLocked} />

        {/* This customer's OTHER conversations, closed ones included (D7). Collapsed by default: no request until opened. */}
        <PastConversations conv={conv} />

        <div className="ac-ctx-sec">
          <button
            type="button"
            className="ac-ctx-sec-toggle"
            onClick={toggleContact}
            aria-expanded={contactOpen}
            data-testid="ctx-contact-toggle"
          >
            <span className="ac-ctx-sec-title">{t('cp_contact_details')}</span>
            <Icon name="chevron" size={14} />
          </button>

          {contactOpen && (
            <div className="cp-sec-body">
              <ContactSummary
                customerId={conv.customerId}
                // getCustomerDisplayName, NOT getConversationLabel: for an email conversation the thread label is
                // the SUBJECT LINE, which is a fine title and plainly wrong as a person's name.
                fallbackName={getCustomerDisplayName(conv)}
                profile={profileState.profile}
                anonymous={anon}
                loading={profileState.loading}
                notFound={profileState.notFound}
                variant="panel"
                avatarSize={48}
              />

              {/*
                No contact page for an anonymous visitor: there is no contact record behind them, so the button
                would lead somewhere that can only apologise. ContactSummary already says they are unidentified.
              */}
              {!anon && (
                <button
                  type="button"
                  className="ac-btn ghost sm"
                  onClick={() => navigate(`/contacts/${encodeURIComponent(conv.customerId)}`)}
                  data-testid="ctx-view-contact"
                  style={{ marginBlockStart: 10, width: '100%' }}
                >
                  <Icon name="user" size={14} />
                  {t('ctx_view_contact')}
                </button>
              )}
            </div>
          )}
        </div>

        {/* 2026-10-31 - at the very bottom: the ids to paste into the Logs page (operator's item 1). */}
        <TraceIds sessionId={conv.sessionId} />
      </div>
    </aside>
  );
};
